import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronRight, FaSearch } from 'react-icons/fa';
import AvatarCircle from '../Utils/AvatarCircle';

/**
 * Lire une équipe : le responsable au-dessus de la personne, puis ses
 * collaborateurs directs en lignes compactes, filtrables par statut du jour
 * et, au-delà de `SEUIL_RECHERCHE`, cherchables par nom.
 *
 * Partagé par « Mon équipe » de l'espace collaborateur (Yros/Kavero#488) et
 * l'onglet « Équipe » de la fiche RH (Yros/Kavero#494) : la RH et les
 * salariés lisent l'organigramme de la même façon.
 */

export const SEUIL_RECHERCHE = 8;

// Ce qui demande l'attention du manager remonte en premier.
const ORDRE_STATUTS = ['pas_pointe', 'conge', 'present', 'parti'];

const PASTILLES = {
  pas_pointe: 'bg-amber-50 text-amber-700',
  conge: 'bg-sky-50 text-sky-700',
  present: 'bg-emerald-50 text-emerald-700',
  parti: 'bg-slate-100 text-slate-600',
};

export const statutDe = (equipier) => (equipier.conge_jusquau ? 'conge' : equipier.presence || 'pas_pointe');

export const jourMois = (iso, i18n) => new Intl.DateTimeFormat(i18n?.language || 'fr-FR',
  { day: '2-digit', month: '2-digit' }).format(new Date(`${iso}T00:00:00`));

const sansAccent = (texte) => (texte || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function Personne({ nom, poste, accent, testId, onOuvrir, children }) {
  const Balise = onOuvrir ? 'button' : 'div';
  return (
    <Balise
      {...(onOuvrir ? { type: 'button', onClick: onOuvrir } : {})}
      data-testid={testId}
      className={`w-full sm:w-72 p-2.5 sm:p-3 rounded-xl border flex items-center gap-3 text-left ${
        accent ? 'bg-white border-[--color-primary]' : 'bg-slate-50/70 border-slate-100'
      } ${onOuvrir ? 'hover:bg-slate-100 transition-colors' : ''}`}
    >
      <AvatarCircle name={nom} size={36} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800 break-words">{nom}</p>
        {poste && <p className="text-xs text-slate-500 break-words">{poste}</p>}
        {children}
      </div>
    </Balise>
  );
}

/**
 * Le responsable au-dessus de la personne. `onOuvrir` reçoit l'id du
 * responsable : la fiche de l'annuaire côté salarié, la fiche RH côté RH.
 * `libelleResponsable` : « Mon responsable » par défaut, « Responsable » sur
 * la fiche d'un autre.
 */
export function Organigramme({ responsable, moi, onOuvrir, libelleResponsable }) {
  const { t } = useTranslation();
  return (
    <div data-testid="mon-organigramme" className="flex flex-col items-center">
      {responsable && (
        <>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
            {libelleResponsable || t('monEquipe.responsable')}
          </p>
          {/* L'e-mail et le reste sont dans sa fiche : toute la carte l'ouvre. */}
          <Personne nom={responsable.nom} poste={responsable.poste} testId="mon-responsable"
            onOuvrir={responsable.id && onOuvrir ? () => onOuvrir(responsable.id) : undefined} />
          <span className="w-px h-4 bg-slate-300" aria-hidden="true" />
        </>
      )}
      <Personne nom={moi.nom || t('monEquipe.moi')} poste={moi.poste} accent testId="organigramme-moi" />
    </div>
  );
}

function Ligne({ equipier, onOuvrir }) {
  const { t, i18n } = useTranslation();
  const statut = statutDe(equipier);
  const date = statut === 'conge' ? jourMois(equipier.conge_jusquau, i18n) : null;
  // Court dans la ligne, qui doit laisser la place au nom ; en entier au survol.
  const libelleStatut = t(`monEquipe.pastille.${statut}`, { date });
  const libelleComplet = statut === 'conge'
    ? t('monEquipe.enConge', { date })
    : t(`monEquipe.presence.${statut}`);

  return (
    <li data-testid={`equipier-${equipier.id}`} data-statut={statut} className="border-b border-slate-100">
      <button
        type="button"
        onClick={() => onOuvrir(equipier.id)}
        className="w-full flex items-center gap-3 py-2.5 px-1 text-left hover:bg-slate-50 rounded-lg"
      >
        {equipier.photo ? (
          <img src={`data:image/png;base64,${equipier.photo}`} alt=""
            className="w-9 h-9 rounded-full object-cover shrink-0" />
        ) : (
          <AvatarCircle name={equipier.nom} size={36} />
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-slate-800 truncate">{equipier.nom}</span>
          {equipier.poste && <span className="block text-xs text-slate-500 truncate">{equipier.poste}</span>}
          {equipier.nb_collaborateurs > 0 && (
            <span className="block text-xs text-[--color-primary]">
              {t('equipeCollaborateur.dirige', { n: equipier.nb_collaborateurs })}
            </span>
          )}
        </span>
        <span title={libelleComplet} className={`shrink-0 whitespace-nowrap text-xs font-medium px-2 py-1 rounded-full ${PASTILLES[statut]}`}>
          {libelleStatut}
        </span>
        <FaChevronRight aria-hidden="true" className="shrink-0 w-3 h-3 text-slate-400" />
      </button>
    </li>
  );
}

export function ListeEquipe({ equipe, onOuvrir }) {
  const { t } = useTranslation();
  const [filtre, setFiltre] = useState('tous');
  const [recherche, setRecherche] = useState('');

  const comptes = useMemo(() => equipe.reduce((acc, e) => {
    const statut = statutDe(e);
    return { ...acc, [statut]: (acc[statut] || 0) + 1 };
  }, {}), [equipe]);

  const visibles = useMemo(() => {
    const cherche = sansAccent(recherche.trim());
    return equipe
      .filter((e) => filtre === 'tous' || statutDe(e) === filtre)
      .filter((e) => !cherche || sansAccent(e.nom).includes(cherche))
      .sort((a, b) => ORDRE_STATUTS.indexOf(statutDe(a)) - ORDRE_STATUTS.indexOf(statutDe(b))
        || a.nom.localeCompare(b.nom, 'fr'));
  }, [equipe, filtre, recherche]);

  const filtres = [['tous', equipe.length], ...ORDRE_STATUTS
    .filter((statut) => comptes[statut]).map((statut) => [statut, comptes[statut]])];

  return (
    <div className="space-y-3">
      <div role="group" aria-label={t('monEquipe.filtrer')} className="flex flex-wrap gap-2">
        {filtres.map(([cle, n]) => (
          <button
            key={cle}
            type="button"
            data-testid={`equipe-filtre-${cle}`}
            aria-pressed={filtre === cle}
            onClick={() => setFiltre(cle)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              filtre === cle
                ? 'bg-[--color-primary] text-white border-[--color-primary]'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {t(`monEquipe.filtres.${cle}`)} {n}
          </button>
        ))}
      </div>
      {equipe.length > SEUIL_RECHERCHE && (
        <label className="relative block">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            data-testid="equipe-recherche"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder={t('monEquipe.rechercher')}
            aria-label={t('monEquipe.rechercher')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[--color-primary]"
          />
        </label>
      )}
      {visibles.length === 0 ? (
        <p data-testid="equipe-aucun-resultat" className="py-4 text-center text-sm text-slate-500">
          {t('monEquipe.aucunResultat')}
        </p>
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-6">
          {visibles.map((equipier) => (
            <Ligne key={equipier.id} equipier={equipier} onOuvrir={onOuvrir} />
          ))}
        </ul>
      )}
    </div>
  );
}
